import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useLoad } from '../hooks';
import { PageHead } from '../Layout';
import { formatDateTime, formatRelative, formatStoredPublishError } from '../format';
import { compareArticleHandoff, hasTrendArticlePriority, isActiveOutputType } from '../../shared/types';
import type {
  DeliveryStatus,
  IdeaKind,
  IdeaStage,
  IdeaSummary,
  ScoreComponents,
} from '../../shared/types';

type StageFilter = '' | IdeaStage;
type ConditionFilter = '' | 'attention' | DeliveryStatus;
type ScoreFilter = '' | 'scored' | 'unscored';
type SortKey = 'priority' | 'updated' | 'newest' | 'oldest' | 'score';
type GroupKey = 'none' | 'kind' | 'stage';

interface QueueOutput {
  id: number;
  format: IdeaKind;
  deliveryStatus: DeliveryStatus;
  title: string | null;
  bodyMd: string | null;
  xUrl: string | null;
  publishError: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

export interface QueueItem extends IdeaSummary {
  excerptCount: number;
  candidateText: string | null;
  candidateChars: number | null;
  /** Expected Publisher handoff time; scheduledAt remains the FIFO timestamp. */
  scheduledFor: string | null;
  output: QueueOutput | null;
}

export const DIMENSIONS: { key: keyof ScoreComponents; label: string }[] = [
  { key: 'originality', label: 'Originality' },
  { key: 'specificity', label: 'Specificity' },
  { key: 'resonance', label: 'Resonance' },
  { key: 'density', label: 'Density' },
];

const STAGES: Array<{ value: StageFilter; label: string }> = [
  { value: '', label: 'All stages' },
  { value: 'incubating', label: 'Incubating' },
  { value: 'needs_approval', label: 'Needs approval' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'published', label: 'Published' },
];
const CONDITIONS: Array<{ value: ConditionFilter; label: string }> = [
  { value: '', label: 'Any condition' },
  { value: 'attention', label: 'Needs attention' },
  { value: 'pending', label: 'Pending delivery' },
  { value: 'publishing', label: 'Publishing' },
  { value: 'failed', label: 'Failed' },
];

const TYPES = [{ value: '', label: 'Current outputs' }, { value: 'article', label: 'Articles' }, { value: 'tweet', label: 'Tweets' }, { value: 'quote_tweet', label: 'Quote tweets (history)' }, { value: 'reply', label: 'Replies (history)' }];
const SCORING = [{ value: '', label: 'Any' }, { value: 'scored', label: 'Scored' }, { value: 'unscored', label: 'Not scored' }];
const SORTS = [{ value: 'priority', label: 'Priority' }, { value: 'updated', label: 'Recently updated' }, { value: 'newest', label: 'Newest' }, { value: 'oldest', label: 'Oldest' }, { value: 'score', label: 'Highest score' }];
const GROUPS = [{ value: 'none', label: 'No groups' }, { value: 'kind', label: 'Output type' }, { value: 'stage', label: 'Stage' }];

const PRIORITY: Record<IdeaStage, number> = {
  scheduled: 0,
  needs_approval: 1,
  incubating: 2,
  published: 3,
};

export function num(v: string | number | null | undefined, digits = 2): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : '—';
}

export function ScoreBar({
  value,
  label,
  width = 48,
}: {
  value: number | null;
  label: string;
  width?: number;
}) {
  const percent = value === null ? 0 : Math.max(0, Math.min(10, value)) * 10;
  return (
    <div
      className="score-bar"
      style={{ width }}
      role="img"
      aria-label={`${label}: ${value === null ? 'unscored' : `${value} out of 10`}`}
      title={`${label}: ${value === null ? 'unscored' : `${value}/10`}`}
    >
      <div style={{ width: `${percent}%` }} />
    </div>
  );
}

function StageChip({ stage }: { stage: IdeaStage }) {
  return (
    <span
      className={`chip${stage === 'scheduled' || stage === 'published' ? ' on' : ''}`}
    >
      {stage.replace('_', ' ')}
    </span>
  );
}

function SelectControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

function displayText(item: QueueItem): string {
  return (
    item.output?.title?.trim() ||
    item.output?.bodyMd?.trim() ||
    item.candidateText?.trim() ||
    item.title?.trim() ||
    '(untitled)'
  );
}

function scoreValue(item: QueueItem): number | null {
  if (item.score === null) return null;
  const value = Number(item.score);
  return Number.isFinite(value) ? value : null;
}

export function sortQueue(items: QueueItem[], sort: SortKey): QueueItem[] {
  return [...items].sort((a, b) => {
    if (sort === 'score') return (scoreValue(b) ?? -1) - (scoreValue(a) ?? -1);
    if (sort === 'newest') return Date.parse(b.createdAt) - Date.parse(a.createdAt);
    if (sort === 'oldest') return Date.parse(a.createdAt) - Date.parse(b.createdAt);
    if (sort === 'updated') {
      return Date.parse(b.output?.updatedAt ?? b.updatedAt) - Date.parse(a.output?.updatedAt ?? a.updatedAt);
    }

    const timely = Number(hasTrendArticlePriority(b)) - Number(hasTrendArticlePriority(a));
    if (timely) return timely;
    if (a.stage === 'scheduled' && b.stage === 'scheduled') return compareArticleHandoff(a, b);
    const stage = PRIORITY[a.stage] - PRIORITY[b.stage];
    if (stage !== 0) return stage;
    const score = (scoreValue(b) ?? -1) - (scoreValue(a) ?? -1);
    if (score !== 0) return score;
    return Date.parse(a.createdAt) - Date.parse(b.createdAt);
  });
}

/** Retired formats stay available through their explicit history filters. */
export function queueTypeVisible(itemKind: IdeaKind, selectedKind: IdeaKind | ''): boolean {
  return selectedKind ? itemKind === selectedKind : isActiveOutputType(itemKind);
}

function groupLabel(item: QueueItem, group: GroupKey): string {
  if (group === 'kind') {
    if (item.kind === 'article') return 'Articles';
    if (item.kind === 'tweet') return 'Tweets';
    return item.kind === 'quote_tweet' ? 'Quote tweets · history' : 'Replies · history';
  }
  if (group === 'stage') return item.stage.replace('_', ' ');
  return '';
}

function clip(text: string, length = 140): string {
  const compact = text.replace(/\s+/g, ' ').trim();
  return compact.length > length ? `${compact.slice(0, length)}…` : compact;
}

export default function Queue() {
  const navigate = useNavigate();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [kind, setKind] = useState<IdeaKind | ''>('');
  const [stage, setStage] = useState<StageFilter>('');
  const [condition, setCondition] = useState<ConditionFilter>('');
  const [scoring, setScoring] = useState<ScoreFilter>('');
  const [sort, setSort] = useState<SortKey>('priority');
  const [group, setGroup] = useState<GroupKey>('none');
  const [search, setSearch] = useState('');
  const [scheduleTimezone, setScheduleTimezone] = useState<string | null>(null);
  const currentKind = useRef(kind);
  const requestId = useRef(0);
  currentKind.current = kind;

  const { loading, error } = useLoad(
    useCallback(async () => {
      const requestedKind = kind;
      const requestedId = ++requestId.current;
      const query = new URLSearchParams({ limit: '500' });
      if (requestedKind) query.set('kind', requestedKind);
      const queue = await api.get<{ items: QueueItem[]; scheduleTimezone?: string }>(`/ideas?${query}`);
      if (requestedId !== requestId.current || requestedKind !== currentKind.current) return;
      setItems(queue.items);
      setScheduleTimezone(queue.scheduleTimezone ?? null);
    }, [kind]),
    { pollMs: 15_000 },
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return sortQueue(
      items.filter((item) => {
        const itemStage = item.stage;
        const delivery = item.output?.deliveryStatus;
        const score = scoreValue(item);
        if (!queueTypeVisible(item.kind, kind)) return false;
        if (stage && itemStage !== stage) return false;
        if (condition === 'attention' && delivery !== 'failed') return false;
        if (condition && condition !== 'attention' && delivery !== condition) return false;
        if (scoring === 'scored' && score === null) return false;
        if (scoring === 'unscored' && score !== null) return false;
        if (!needle) return true;
        return [displayText(item), item.title, item.coreClaim, item.output?.publishError]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(needle));
      }),
      sort,
    );
  }, [condition, items, kind, scoring, search, sort, stage]);

  const scopeTotal = useMemo(
    () => items.filter((item) => queueTypeVisible(item.kind, kind)).length,
    [items, kind],
  );

  const groups = useMemo(() => {
    const result = new Map<string, QueueItem[]>();
    for (const item of visible) {
      const label = groupLabel(item, group);
      result.set(label, [...(result.get(label) ?? []), item]);
    }
    return [...result.entries()];
  }, [group, visible]);

  const filtered = Boolean(kind || stage || condition || scoring || search || sort !== 'priority' || group !== 'none');

  function reset() {
    setKind('');
    setStage('');
    setCondition('');
    setScoring('');
    setSort('priority');
    setGroup('none');
    setSearch('');
  }

  return (
    <>
      <PageHead
        title="Queue"
        subtitle={`Read-only history of the legacy queue.${scheduleTimezone ? ` Recorded live times use ${scheduleTimezone}.` : ''}`}
      />

      <p className="editorial-alert">
        This queue is preserved as history. Manage current work in <Link to="/dashboard">Publications</Link>.
      </p>

      <div className="queue-query" aria-label="Queue controls">
        <label className="queue-search">
          <span>Find</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Title, text, or error" />
        </label>
        <SelectControl label="Type" value={kind} options={TYPES} onChange={(value) => setKind(value as IdeaKind | '')} />
        <SelectControl label="Stage" value={stage} options={STAGES} onChange={(value) => setStage(value as StageFilter)} />
        <SelectControl label="Condition" value={condition} options={CONDITIONS} onChange={(value) => setCondition(value as ConditionFilter)} />
        <SelectControl label="Score" value={scoring} options={SCORING} onChange={(value) => setScoring(value as ScoreFilter)} />
        <SelectControl label="Sort" value={sort} options={SORTS} onChange={(value) => setSort(value as SortKey)} />
        <SelectControl label="Group" value={group} options={GROUPS} onChange={(value) => setGroup(value as GroupKey)} />
      </div>

      <div className="queue-result-bar">
        <span>{visible.length} of {scopeTotal} outputs</span>
        {filtered ? <button className="queue-reset" onClick={reset}>Clear view</button> : null}
      </div>

      {error ? <p className="fail">{error}</p> : null}
      {loading ? (
        <div className="empty">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="empty">No outputs match this view.</div>
      ) : (
        groups.map(([label, rows]) => (
          <section className="queue-group" key={label || 'all'}>
            {label ? <h2>{label}<span>{rows.length}</span></h2> : null}
            <div className="queue-list">
              {rows.map((item) => {
                const itemStage = item.stage;
                const activeFormat = isActiveOutputType(item.kind);
                const score = scoreValue(item);
                const updatedAt = item.output?.updatedAt ?? item.updatedAt;
                // Scheduled article packages have their own expanded surface:
                // it shows the canonical prepared article when composition has
                // completed, alongside the package's score and source record.
                const destination =
                  item.kind === 'article' && item.stage === 'scheduled' && item.output?.deliveryStatus !== 'failed'
                    ? `/ideas/${item.id}`
                    : item.output
                      ? `/articles/${item.output.id}`
                      : `/ideas/${item.id}`;
                return (
                  <div
                    className="queue-row"
                    key={item.id}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(destination)}
                    onKeyDown={(event) => { if (event.key === 'Enter') navigate(destination); }}
                  >
                      <div className="queue-kind"><span className="chip">{item.kind}</span></div>
                      <div className="queue-title">
                        <div className="queue-title-text">{clip(displayText(item), item.kind === 'article' ? 150 : 280)}</div>
                        {item.output?.deliveryStatus === 'failed' && item.output.publishError ? (
                          <div className="fail queue-claim" title={item.output.publishError}>
                            {clip(activeFormat ? formatStoredPublishError(item.output.publishError) : item.output.publishError)}
                          </div>
                        ) : item.output && item.candidateText && item.candidateText.trim() !== displayText(item).trim() ? (
                          <div className="muted queue-claim">From idea #{item.id}: {clip(item.candidateText)}</div>
                        ) : item.coreClaim && item.coreClaim !== displayText(item) ? (
                          <div className="muted queue-claim">{clip(item.coreClaim)}</div>
                        ) : null}
                        {item.kind !== 'article' && item.candidateChars !== null ? (
                          <div className="faint mono queue-measure">{item.candidateChars} characters</div>
                        ) : null}
                      </div>
                      <div className="queue-status">
                        {activeFormat ? <StageChip stage={itemStage} /> : <span className="chip" title={`Recorded stage: ${itemStage}`}>Historical</span>}
                        {hasTrendArticlePriority(item) ? <span className="chip" title="Trend article: priority in the article queue">Timely</span> : null}
                        {item.output?.deliveryStatus === 'failed' ? <span className="fail">Delivery failed</span> : null}
                        {activeFormat && item.output?.deliveryStatus === 'publishing' ? <span className="faint">Publishing…</span> : null}
                        {item.output?.xUrl ? (
                          <a
                            className="queue-x-link"
                            href={item.output.xUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                          >
                            Open on X ↗
                          </a>
                        ) : null}
                      </div>
                      <div className="queue-score mono">
                        {score === null ? <span className="faint">Not scored</span> : num(score)}
                      </div>
                      <div className="queue-activity">
                        <span>{item.excerptCount} {item.excerptCount === 1 ? 'source' : 'sources'}</span>
                        <span className="queue-activity-separator" aria-hidden="true">·</span>
                        <span title={formatDateTime(updatedAt)}>Updated {formatRelative(updatedAt)}</span>
                        {item.output?.publishedAt ? (
                          <><span className="queue-activity-separator" aria-hidden="true">·</span><span>Published {formatRelative(item.output.publishedAt)}</span></>
                        ) : null}
                        {activeFormat && item.stage === 'scheduled' && item.scheduledFor && item.output?.deliveryStatus !== 'failed' ? (
                          <><span className="queue-activity-separator" aria-hidden="true">·</span><span title={formatDateTime(item.scheduledFor, { timeZone: scheduleTimezone ?? undefined })}>Goes live {formatDateTime(item.scheduledFor, { timeZone: scheduleTimezone ?? undefined })}</span></>
                        ) : null}
                      </div>
                      <div className="queue-actions"><span className="faint">Read only</span></div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </>
  );
}
